import { Component, inject } from '@angular/core';
import { UsersStore } from '@data-access/user/stores/users.store';
import { FormBuilder, FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { UserData } from '@shared/components/user-data/user-data';
import { MatButton } from '@angular/material/button';
import { firstValueFrom } from 'rxjs';
import { mapFormToPayload, UserDataFormValue } from '@shared/helpers/user-form-to-payload';
import { MatProgressBar } from '@angular/material/progress-bar';
import { MatTab, MatTabGroup } from '@angular/material/tabs';
import { confirmPasswordValidator } from '@shared/validators/password-match.validator';
import { MatIcon } from '@angular/material/icon';
import { ConfirmationDialog } from '@shared/components/confirmation-dialog/confirmation-dialog';
import { MatDialog } from '@angular/material/dialog';
import { PlanSelectDialog } from '@shared/components/plan-select-dialog/plan-select-dialog';
import { BillingHistoryItem } from '@shared/interfacres/user';
import { DatePipe } from '@angular/common';
import { DemoUserHandler } from '@shared/services/demo-user-handler';
import { Router } from '@angular/router';

@Component({
  selector: 'app-profile',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    UserData,
    MatButton,
    MatProgressBar,
    MatTabGroup,
    MatTab,
    MatIcon,
    DatePipe
  ],
  templateUrl: './profile.html',
  styleUrl: './profile.scss'
})
export class Profile {
  readonly userStore = inject(UsersStore);
  readonly dialog = inject(MatDialog);
  readonly demoUserHandlerService = inject(DemoUserHandler);
  readonly router = inject(Router);

  billingHistory: BillingHistoryItem[] = [];

  currentSubscriptionId = '';

  fb = inject(FormBuilder);
  disabled = false;

  performanceTypes: string[] = [];
  isPasswordShown = false;

  form = this.fb.group({
    userData: {
      firstName: this.userStore.user()!.firstName || '',
      lastName: this.userStore.user()!.lastName || '',
      stageName: this.userStore.user()!.stageName || '',

      performanceType: this.userStore.user()!.performanceType || [],
      weeklyPerformances: this.userStore.user()!.weeklyPerformances || 0,

      website: this.userStore.user()!.socialLinks?.website || '',
      instagram: this.userStore.user()!.socialLinks?.instagram || '',
      facebook: this.userStore.user()!.socialLinks?.facebook || '',
      performanceLocations1: this.userStore.user()!.performanceLocations?.length ? this.userStore.user()!.performanceLocations![0] : '',
      performanceLocations2: this.userStore.user()!.performanceLocations?.length &&
        this.userStore.user()!.performanceLocations![1] ? this.userStore.user()!.performanceLocations![1] : '',
      performanceLocations3: this.userStore.user()!.performanceLocations?.length &&
        this.userStore.user()!.performanceLocations![2] ? this.userStore.user()!.performanceLocations![2] : '',
      // temps: [{
      //   id: 2,
      //   name: 'Temp 2',
      //   selected: true
      // }]
    },
  });

  changePasswordForm = this.fb.group({
    oldPassword: new FormControl('', [Validators.required, Validators.minLength(6), Validators.pattern(/^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[\W_]).*$/)]),
    password: new FormControl('', [Validators.required, Validators.minLength(6), Validators.pattern(/^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[\W_]).*$/)]),
    passwordConfirm: new FormControl(''),
  }, { validators: confirmPasswordValidator })

  constructor() {
    if (!this.userStore.user()) {
      this.userStore.logout();
    }
    this.init();
  }


  async init() {
    const isUserValid = await this.demoUserHandlerService.isUserValid();

    if (!isUserValid) {
      this.router.navigate(['/login']);
      return;
    }
    this.performanceTypes = await firstValueFrom(this.userStore.getPerformanceTypes());
  }

  save() {
    const payload = mapFormToPayload(this.form.value.userData as UserDataFormValue)
    this.userStore.update(payload);
  }

  async changePassword() {
    if (!this.changePasswordForm.value.oldPassword || !this.changePasswordForm.value.password) {
      return;
    }
    const res = await this.userStore.changePassword(this.changePasswordForm.value.oldPassword!, this.changePasswordForm.value.password!);

    if (res && res.success) {
      this.dialog.open(ConfirmationDialog, {
        data: {
          title: 'Success',
          message: 'Password was changed successfully, now, please, login with new credentials',
          hideCancel: true
        },
        disableClose: true,
        panelClass: 'admin-dialog',
        backdropClass: 'admin-dialog-backdrop'
      });
      this.userStore.logout();
    }
  }

  async resetSubscription() {
    const dialogRef = this.dialog.open(ConfirmationDialog, {
      data: {
        title: 'Confirm',
        message: 'Thank you for being with us! If you cancel today, your subscription will remain until your billing cycle ends. Do you want to confirm cancellation?'
      },
      disableClose: true,
      panelClass: 'admin-dialog',
      backdropClass: 'admin-dialog-backdrop'
    });
    const result = await firstValueFrom(dialogRef.afterClosed());
    if (result) {

      await this.userStore.cancelSubscription();
      this.userStore.logout()
    }

  }

  async changeSubscription() {
    const plans = await firstValueFrom(this.userStore.getPlans());
    const dialogRef = this.dialog.open(PlanSelectDialog, {
      data: {
        activePlanId: this.currentSubscriptionId,
        plans: plans,
        showCancel: true
      },
      disableClose: true,
      width: '85%',
      maxWidth: '1500px',
      panelClass: 'admin-dialog',
      backdropClass: 'admin-dialog-backdrop'
    });

    const result = await firstValueFrom(dialogRef.afterClosed());
    if (result) {
      this.currentSubscriptionId = '';
      this.billingHistory = [];

      await this.userStore.changeSubscriptionPlan(result);
      this.getCurrentSubscription();
    }

  }

  async changeTab(tabIndex: number) {
    if (tabIndex === 1) {

      this.getCurrentSubscription();

    }
    if (tabIndex === 2) {
      if (this.billingHistory.length === 0) {
        const res: { billingHistory: BillingHistoryItem[] } = await this.userStore.getBillingHistory();
        this.billingHistory = res.billingHistory;
      }
    }
  }

  async getCurrentSubscription() {
    const res: {productId: string} = await this.userStore.getCurrentSubscription();

    if (!res) {
      // something went wrong
      this.userStore.logout();
    }

    this.currentSubscriptionId = res.productId;

  }

  reset() {
    this.form.setValue({
      userData: {
        firstName: this.userStore.user()!.firstName || '',
        lastName: this.userStore.user()!.lastName || '',
        stageName: this.userStore.user()!.stageName || '',

        performanceType: this.userStore.user()!.performanceType || [],
        weeklyPerformances: this.userStore.user()!.weeklyPerformances || 0,

        website: this.userStore.user()!.socialLinks?.website || '',
        instagram: this.userStore.user()!.socialLinks?.instagram || '',
        facebook: this.userStore.user()!.socialLinks?.facebook || '',
        performanceLocations1: this.userStore.user()!.performanceLocations?.length ? this.userStore.user()!.performanceLocations![0] : '',
        performanceLocations2: this.userStore.user()!.performanceLocations?.length &&
        this.userStore.user()!.performanceLocations![1] ? this.userStore.user()!.performanceLocations![1] : '',
        performanceLocations3: this.userStore.user()!.performanceLocations?.length &&
        this.userStore.user()!.performanceLocations![2] ? this.userStore.user()!.performanceLocations![2] : '',
        // temps: []
      },
    });
  }

}
